import { useState, useCallback, useRef, useEffect } from 'react';
import { EventState, EventDetails, UserData, FormattedActivity } from '../types/event.types';
import { toast } from 'react-toastify';
import { debounce, safeAsync, hasUserRole } from '../utils/eventUtils';
import { getLoginData } from '../../../../../utility/AppStoreData';
import { cancelEvent, fetchEventDetails, updateEvent } from '../../../../../services/eventsAPI';
import { fetchStreamDetailsYoutube } from '../../../../../services/stream';
import { getEventActivities, ACTIVITY_TYPES, UserActivity } from '../../../../../services/activityService';
import { useNotifications } from '../../../../../context/NotificationContext';

export const useEventData = (eventID: string) => {
  const [eventState, setEventState] = useState<EventState>({
    details: null,
    loading: false,
    error: null,
    updateLoading: false,
    activities: [],
  });

  const [userData, setUserData] = useState<UserData | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  // Get activity updates from notification context
  const { activityUpdate } = useNotifications();

  // Helper to format time as relative time (for real-time updates)
  const formatRelativeTime = (timestamp: string): string => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMs = now.getTime() - activityTime.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    if (diffHours < 24) return `${diffHours} hr ago`;
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  };

  // Listen for real-time activity updates via WebSocket
  useEffect(() => {
    if (!activityUpdate || !isMountedRef.current) return;

    // Only append if the activity is for the current event
    if (activityUpdate.eventId !== eventID) return;

    // Format the new activity
    const newActivity: FormattedActivity = {
      username: activityUpdate.userId,
      profilePicture: activityUpdate.userProfilePicture || undefined,
      activity: activityUpdate.activityMessage || activityUpdate.activityType?.replace(/_/g, " ") || "Unknown",
      time: formatRelativeTime(activityUpdate.createdAt),
    };

    // Append to the beginning of the activities list (most recent first)
    setEventState(prev => ({
      ...prev,
      activities: [newActivity, ...prev.activities],
    }));
  }, [activityUpdate, eventID]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const updateEventState = useCallback((updates: Partial<EventState>) => {
    if (!isMountedRef.current) return;
    setEventState(prev => ({ ...prev, ...updates }));
  }, []);

  const handleError = useCallback((error: any, context: string) => {
    console.error(`${context}:`, error);
    const errorMessage = error?.message || error?.body?.error || 'An unexpected error occurred';

    if (!isMountedRef.current) return;
    setEventState(prev => ({ ...prev, error: errorMessage, loading: false, updateLoading: false }));

    if (error?.statusCode === 400) {
      toast.error("Bad Request");
    } else if (error?.statusCode > 400 && error?.statusCode < 420) {
      toast.error("Unauthorized");
    } else if (error?.statusCode >= 500) {
      toast.error("Internal Server Error");
    } else {
      toast.error(errorMessage);
    }
  }, []);

  const getLoginUserData = useCallback(async (): Promise<UserData | null> => {
    try {
      const data = await getLoginData();
      if (data && !isMountedRef.current) return null;
      setUserData(data);
      return data;
    } catch (error) {
      console.error('Failed to get login data:', error);
      if (isMountedRef.current) {
        setEventState(prev => ({ ...prev, error: 'Failed to get login data', loading: false }));
        toast.error('Failed to get login data');
      }
      return null;
    }
  }, []);

  // Helper to format activity type to readable string
  const getActivityLabel = (activityType: string, metadata?: any): string => {
    const labels: Record<string, string> = {
      [ACTIVITY_TYPES.CHECKED_IN]: "Checked in successfully",
      [ACTIVITY_TYPES.PLATFORM_CONNECTED]: metadata?.platforms
        ? `Connected ${(metadata.platforms as string[]).join(" & ")}`
        : "Connected streaming platform",
      [ACTIVITY_TYPES.CONFIRMED_ATTENDANCE]: "Confirmed attendance",
      [ACTIVITY_TYPES.ENTERED_STUDIO]: "Entered studio",
      [ACTIVITY_TYPES.STREAM_STARTED]: "Started streaming",
      [ACTIVITY_TYPES.STREAM_ENDED]: "Ended stream",
      [ACTIVITY_TYPES.CHALLENGE_ACCEPTED]: "Accepted challenge",
      [ACTIVITY_TYPES.CHALLENGE_REJECTED]: "Rejected challenge",
      [ACTIVITY_TYPES.CHALLENGE_SENT]: "Sent challenge",
      [ACTIVITY_TYPES.EVENT_CREATED]: "Created event",
      [ACTIVITY_TYPES.BROADCASTER_DOWNLOADED]: "Downloaded KO Broadcaster",
      [ACTIVITY_TYPES.JOINED_LOBBY]: "Joined lobby",
      [ACTIVITY_TYPES.NEGOTIATION_STARTED]: "Started negotiation",
      [ACTIVITY_TYPES.TERMS_MODIFIED]: "Modified terms",
      [ACTIVITY_TYPES.OPPONENT_CHANGED]: "Changed opponent",
      [ACTIVITY_TYPES.CHAT_MESSAGE_SENT]: "Sent a message",
      [ACTIVITY_TYPES.EVENT_COMPLETED]: "Event completed",
      [ACTIVITY_TYPES.EVENT_CANCELLED]: "Event cancelled",
    };
    return labels[activityType] || activityType.replace(/_/g, " ");
  };

  // Helper to format time as relative time
  const getRelativeTime = (timestamp: string): string => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMs = now.getTime() - activityTime.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    if (diffHours < 24) return `${diffHours} hr ago`;
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  };

  // Fetch activities for the event
  const fetchActivitiesForEvent = useCallback(async (
    accessToken: string,
    eventId: string,
    participantDetails?: { userID: string; profilePicture?: string }[]
  ) => {
    try {
      const result = await getEventActivities(accessToken, eventId);

      if (!isMountedRef.current) return;

      if (result.success && result.activities) {
        const formattedActivities: FormattedActivity[] = result.activities.map((activity: UserActivity) => {
          // Find the participant's profile picture from participantDetails
          const participant = participantDetails?.find(
            (p) => p.userID === activity.userId
          );

          return {
            username: activity.userId,
            profilePicture: participant?.profilePicture || activity.userProfilePicture || undefined,
            activity: getActivityLabel(activity.activityType, activity.metadata),
            time: getRelativeTime(activity.createdAt),
          };
        });

        setEventState(prev => ({ ...prev, activities: formattedActivities }));
      }
    } catch (error) {
      console.warn("Error fetching activities:", error);
    }
  }, []);

  const fetchEventRef = useRef<(eventID: string, force?: boolean, silent?: boolean) => Promise<void>>();

  fetchEventRef.current = async (eventID: string, force = false, silent = false) => {
    if (!eventID) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    if (!silent) {
      if (!force) {
        setEventState(prev => {
          if (prev.loading) return prev;
          return { ...prev, loading: true, error: null };
        });
      } else {
        setEventState(prev => ({ ...prev, loading: true, error: null }));
      }
    }

    try {
      const loginData = userData || await getLoginUserData();
      if (!loginData || !isMountedRef.current) return;

      const response = await fetchEventDetails(eventID, loginData.AccessToken);

      if (!isMountedRef.current) return;

      if (response) {
        const userRole = hasUserRole(loginData.username, response.organiserId, 'organiser')
          ? 'organiser' : 'challenger';

        const youtubeStreamField = userRole === 'organiser'
          ? response.organizerYoutubeStream
          : response.challengerYoutubeStream;

        const embeddedField = userRole === 'organiser'
          ? response.organizerEmbedded
          : response.challengerEmbedded;

        let updatedResponse = response;

        if (youtubeStreamField && !embeddedField) {
          const streamResponse = await safeAsync(() =>
            fetchStreamDetailsYoutube(eventID, loginData.AccessToken)
          );

          if (streamResponse?.statusCode === 200 && streamResponse.body.enableEmbed) {
            const embeddedFieldKey = userRole === 'organiser'
              ? 'organizerEmbedded'
              : 'challengerEmbedded';

            updatedResponse = {
              ...response,
              [embeddedFieldKey]: true,
            };
          }
        }

        setEventState(prev => ({
          ...prev,
          details: updatedResponse,
          loading: silent ? prev.loading : false,
          error: null
        }));

        // Fetch activities after event details are loaded
        fetchActivitiesForEvent(loginData.AccessToken, eventID, updatedResponse.participantDetails);
      } else {
        throw new Error('No event data received');
      }
    } catch (error: any) {
      if (error.name === 'AbortError') return;
      console.error('Failed to fetch event details:', error);
      if (isMountedRef.current) {
        const errorMessage = error?.message || error?.body?.error || 'Failed to fetch event details';
        setEventState(prev => ({ ...prev, error: errorMessage, loading: false }));
        toast.error(errorMessage);
      }
    }
  };

  const fetchEvent = useCallback((eventID: string, force = false, silent = false) => {
    return fetchEventRef.current?.(eventID, force, silent);
  }, []);

  const debouncedFetchEvent = useCallback(
    debounce((eventID: string, force?: boolean) => fetchEvent(eventID, force), 300),
    [fetchEvent]
  );

  const updateEventDetails = useCallback(async (updates: {
    sponsor?: string;
    description?: string;
    organiserPercentage?: number;
    challengerPercentage?: number;
  }) => {
    if (!eventID || !userData) {
      toast.error("Missing required information");
      return false;
    }

    updateEventState({ updateLoading: true });

    try {
      const bodyParams = {
        accessToken: userData.AccessToken,
        eventID: eventID,
        newDetails: updates,
      };

      const response = await updateEvent(bodyParams);

      if (!isMountedRef.current) return false;

      if (response.statusCode === 200 || response.statusCode === 201) {
        toast.success(response.body?.message || "Event updated successfully");
        await fetchEvent(eventID, true);
        updateEventState({ updateLoading: false });
        return true;
      } else {
        throw response;
      }
    } catch (error: any) {
      handleError(error, 'Failed to update event');
      return false;
    }
  }, [eventID, userData, updateEventState, fetchEvent, handleError]);

  const cancelEventAction = useCallback(async (reason: string, message: string) => {
    if (!userData || !eventID) {
      toast.error("Missing required information to cancel event");
      return false;
    }

    if (!reason) {
      toast.error("Please select a cancellation reason");
      return false;
    }

    updateEventState({ updateLoading: true });

    try {
      const response = await cancelEvent(userData.AccessToken, eventID, reason, message);

      if (!isMountedRef.current) return false;

      const { statusCode, body } = response;

      if (statusCode === 200) {
        toast.success(body.message || "Event cancelled successfully");
        await fetchEvent(eventID, true);
        updateEventState({ updateLoading: false });
        return true;
      } else {
        throw response;
      }
    } catch (error: any) {
      handleError(error, 'Failed to cancel event');
      return false;
    }
  }, [userData, eventID, updateEventState, fetchEvent, handleError]);

  const refreshEvent = useCallback(() => {
    fetchEvent(eventID, true);
  }, [fetchEvent, eventID]);

  const silentRefreshEvent = useCallback(() => {
    fetchEvent(eventID, true, true);
  }, [fetchEvent, eventID]);

  useEffect(() => {
    if (eventID) {
      getLoginUserData().then(() => {
        fetchEvent(eventID);
      });
    }
  }, [eventID]);

  return {
    ...eventState,
    userData,
    fetchEvent: debouncedFetchEvent,
    updateEventDetails,
    cancelEventAction,
    refreshEvent,
    silentRefreshEvent,
    isOrganiser: userData && eventState.details ?
      hasUserRole(userData.username, eventState.details.organiserId, 'organiser') : false,
  };
};
